import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, X } from "lucide-react";
import { api } from "../lib/api";
import { storeName } from "../lib/labels";
import DateRangePicker from "../components/DateRangePicker";
import TrendChart from "../components/TrendChart";
import TopProducts from "../components/TopProducts";
import EmergingProducts from "../components/EmergingProducts";
import CategoriesChart from "../components/CategoriesChart";

function money(n) {
  if (n == null) return "—";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

function Delta({ pct }) {
  if (pct == null) return <span className="font-mono-bm text-xs text-neutral-400">—</span>;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const cls = up ? "text-emerald-700" : "text-[#c72c2c]";
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono-bm text-xs font-semibold ${cls}`}>
      <Icon size={12} strokeWidth={2.5} />
      {up ? "+" : ""}{pct}%
    </span>
  );
}

function Kpi({ label, value, delta, sub, testId }) {
  return (
    <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-5" data-testid={testId}>
      <div className="bm-eyebrow text-neutral-500">{label}</div>
      <div className="bm-headline text-3xl mt-2 tabular-nums leading-none">{value}</div>
      <div className="mt-3 flex items-center gap-3">
        <Delta pct={delta} />
        {sub && <span className="bm-eyebrow text-neutral-500">{sub}</span>}
      </div>
    </div>
  );
}

function useStoreData(outletId, range) {
  const [data, setData] = useState({ headline: null, trend: [], top: [], emerging: [], categories: [] });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!range || !outletId) return;
    let cancel = false;
    setLoading(true);
    const params = { start_date: range.start, end_date: range.end, outlet_ids: outletId };
    Promise.all([
      api.get("/metrics/headline-multi", { params }),
      api.get("/products/top", { params: { ...params, limit: 12 } }),
      api.get("/products/emerging", { params }),
      api.get("/metrics/categories", { params: { ...params, limit: 15 } }),
    ]).then(([h, p, em, cat]) => {
      if (cancel) return;
      setData({
        headline: h.data,
        top: p.data || [],
        emerging: em.data || [],
        categories: cat.data || [],
      });
    }).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range?.start, range?.end, outletId]);
  return { data, loading };
}

function extractKpis(headline) {
  if (!headline?.current) return null;
  const cur = headline.current;
  const cmp = headline.comparisons?.[0] || {};
  return {
    revenue: cur.revenue, transactions: cur.transactions, units: cur.units,
    aov: cur.aov, pcs_per_order: cur.pcs_per_order,
    revenue_growth: cmp.growth_pct_revenue,
    transactions_growth: cmp.growth_pct_transactions,
    aov_growth: cmp.growth_pct_aov,
    pcs_growth: cmp.growth_pct_pcs_per_order,
  };
}

function KpiRow({ store, kpis, testPrefix }) {
  if (!kpis) return null;
  return (
    <section className="space-y-2" data-testid={`${testPrefix}-kpi-row`}>
      <div className="bm-eyebrow text-neutral-600">{storeName(store?.name || "Store")}</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Revenue" value={money(kpis.revenue)} delta={kpis.revenue_growth}
             sub={`${(kpis.units || 0).toLocaleString()} units`} testId={`${testPrefix}-kpi-revenue`} />
        <Kpi label="Transactions" value={(kpis.transactions || 0).toLocaleString()}
             delta={kpis.transactions_growth} testId={`${testPrefix}-kpi-transactions`} />
        <Kpi label="Avg. Order" value={money(kpis.aov)} delta={kpis.aov_growth}
             sub="per transaction" testId={`${testPrefix}-kpi-aov`} />
        <Kpi label="Pcs / Order" value={(kpis.pcs_per_order || 0).toFixed(2)}
             delta={kpis.pcs_growth} sub="items per basket" testId={`${testPrefix}-kpi-pcs`} />
      </div>
    </section>
  );
}

export default function StoreDetail() {
  const { outletId } = useParams();
  const navigate = useNavigate();
  const [range, setRange] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [compareId, setCompareId] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get("/outlets").then((r) => setOutlets(r.data || []));
  }, []);

  const primary = useMemo(() => outlets.find((o) => o.id === outletId), [outlets, outletId]);
  const compare = useMemo(() => outlets.find((o) => o.id === compareId), [outlets, compareId]);

  // Fetch trend for both stores in one call (multi-outlet supported by API)
  useEffect(() => {
    if (!range) return;
    let cancel = false;
    const ids = compareId ? `${outletId},${compareId}` : outletId;
    const params = {
      start_date: range.start,
      end_date: range.end,
      outlet_ids: ids,
      granularity: range.granularity || "month",
    };
    api.get("/metrics/trend-with-weather", { params })
      .then((r) => { if (!cancel) setTrend(r.data || []); });
    return () => { cancel = true; };
  }, [range?.start, range?.end, outletId, compareId]);

  const primaryData = useStoreData(outletId, range);
  const compareData = useStoreData(compareId, range);

  const primaryKpis = extractKpis(primaryData.data.headline);
  const compareKpis = extractKpis(compareData.data.headline);

  const otherStores = outlets.filter((o) => o.id !== outletId && o.is_retail);

  const granularityLabel = (range?.granularity || "month").charAt(0).toUpperCase() + (range?.granularity || "month").slice(1);

  return (
    <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-6" data-testid="store-detail">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => navigate("/")}
            data-testid="store-detail-back"
            className="inline-flex items-center gap-1.5 bm-eyebrow text-neutral-600 hover:text-[#0a0a0a] mb-2"
          >
            <ArrowLeft size={13} /> Back to chain
          </button>
          <div className="bm-eyebrow text-neutral-500">Store deep dive</div>
          <h1 className="bm-headline text-4xl mt-1">
            {storeName(primary?.name || "Store")}
            {compare && (
              <span className="text-neutral-400"> <span className="text-neutral-300 font-sans font-normal text-2xl align-middle">vs</span> {storeName(compare.name)}</span>
            )}
            .
          </h1>
          <p className="bm-eyebrow text-neutral-500 mt-1">{primary?.name || ""}{compare ? ` vs ${compare.name}` : ""}</p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <DateRangePicker onChange={setRange} />
          <div className="flex items-center gap-2" data-testid="compare-selector">
            <span className="bm-eyebrow text-neutral-500">Compare vs.</span>
            {!compare ? (
              <select
                onChange={(e) => setCompareId(e.target.value || null)}
                value={compareId || ""}
                data-testid="compare-select"
                className="bm-eyebrow border border-[#0a0a0a] bg-[#f5f2eb] px-2 py-1 pr-6 focus:outline-none"
              >
                <option value="">— pick a store —</option>
                {otherStores.map((o) => (
                  <option key={o.id} value={o.id}>{storeName(o.name)}</option>
                ))}
              </select>
            ) : (
              <button
                onClick={() => setCompareId(null)}
                data-testid="compare-clear"
                className="inline-flex items-center gap-1 bm-eyebrow border border-[#0a0a0a] bg-[#0a0a0a] text-[#f5f2eb] px-2 py-1 hover:opacity-90"
              >
                {storeName(compare.name)}
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* KPIs — one row per store */}
      <KpiRow store={primary} kpis={primaryKpis} testPrefix="store-primary" />
      {compare && <KpiRow store={compare} kpis={compareKpis} testPrefix="store-compare" />}

      {/* Trend (single chart, both stores' lines when comparing) */}
      {trend.length > 0 && (
        <section>
          <TrendChart data={trend} granularity={granularityLabel} />
        </section>
      )}

      {/* Products */}
      {!compare && (
        <section className="grid lg:grid-cols-2 gap-4">
          <TopProducts range={range} initialOutletIds={[outletId]} />
          <EmergingProducts initialOutletIds={[outletId]} />
        </section>
      )}

      {compare && (
        <>
          <section>
            <div className="bm-eyebrow text-neutral-500 mb-2">Best sellers &middot; side by side</div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <div className="bm-eyebrow text-neutral-500 mb-1">{storeName(primary?.name)}</div>
                <TopProducts range={range} initialOutletIds={[outletId]} />
              </div>
              <div>
                <div className="bm-eyebrow text-neutral-500 mb-1">{storeName(compare?.name)}</div>
                <TopProducts range={range} initialOutletIds={[compareId]} />
              </div>
            </div>
          </section>
          <section>
            <div className="bm-eyebrow text-neutral-500 mb-2">On the rise &middot; side by side</div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <div className="bm-eyebrow text-neutral-500 mb-1">{storeName(primary?.name)}</div>
                <EmergingProducts initialOutletIds={[outletId]} />
              </div>
              <div>
                <div className="bm-eyebrow text-neutral-500 mb-1">{storeName(compare?.name)}</div>
                <EmergingProducts initialOutletIds={[compareId]} />
              </div>
            </div>
          </section>
        </>
      )}

      {/* Categories */}
      {!compare && (
        <section>
          <CategoriesChart items={primaryData.data.categories} />
        </section>
      )}
      {compare && (
        <section className="grid lg:grid-cols-2 gap-4">
          <div>
            <div className="bm-eyebrow text-neutral-500 mb-1">{storeName(primary?.name)}</div>
            <CategoriesChart items={primaryData.data.categories} />
          </div>
          <div>
            <div className="bm-eyebrow text-neutral-500 mb-1">{storeName(compare?.name)}</div>
            <CategoriesChart items={compareData.data.categories} />
          </div>
        </section>
      )}
    </div>
  );
}
